import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';
import { syncAnswerLabelText } from '../utils/answerLabelLayout';

import { LottieCanvasSprite } from './LottieCanvasSprite';

import type {
	LaneChooserAnswerAsset,
	LaneChooserAnswerResolution,
	LaneChooserAnswers,
	LaneChooserLottieAsset,
} from '../types';
import type { AnswerBoxPosition } from '../utils/answerLabelLayout';

type AnswerBoxVisual = {
	update: () => void;
	destroy: () => void;
	setDisplaySize: (width: number, height: number) => void;
	setFlipX: (flipX: boolean) => void;
	setVisible: (visible: boolean) => void;
	playFromStart: () => void;
	play: () => void;
	pause: () => void;
};

type AnswerBoxViewConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	x: number;
	position: AnswerBoxPosition;
	index: number;
	labelText: string;
	animationAsset: LaneChooserAnswerAsset;
	themeConfig: LaneChooserAnswers;
	boxWidth: number;
	viewportWidth: number;
	renderScaleX: number;
	renderScaleY: number;
	lottieSourceRoot: HTMLElement;
};

const LABEL_FONT_FAMILY = 'ModernEra, Helvetica, Arial, sans-serif';
const LABEL_FONT_STYLE = '900';
const ANSWER_SUCCESS_TEXT_COLOR = '#DFFF9D';
const ANSWER_FAILED_TEXT_COLOR = '#FFB4B5';
const ANSWER_CORRECT_REVEAL_TEXT_COLOR = '#BBF9FF';
const DELTA_TEXT_COLOR = '#FFFFFF';
const FEEDBACK_FADE_DURATION_MS = 220;

export const CORRECT_ICON_TEXTURE_KEY = 'lane-chooser-correct-icon';
export const INCORRECT_ICON_TEXTURE_KEY = 'lane-chooser-incorrect-icon';
export const CORRECT_ARROW_TEXTURE_KEY = 'lane-chooser-correct-arrow';

function isLottieAnswerAsset(asset: LaneChooserAnswerAsset): asset is LaneChooserLottieAsset {
	return 'animationData' in asset;
}

class StaticAnswerBoxVisual implements AnswerBoxVisual {
	private readonly image: Phaser.GameObjects.Image;

	constructor(scene: Phaser.Scene, parent: Phaser.GameObjects.Container, textureKey: string) {
		this.image = scene.add.image(0, 0, textureKey);
		this.image.setOrigin(0.5, 0.5);
		parent.add(this.image);
	}

	update() {}

	destroy() {
		this.image.destroy();
	}

	setDisplaySize(width: number, height: number) {
		this.image.setDisplaySize(width, height);
	}

	setFlipX(flipX: boolean) {
		this.image.setFlipX(flipX);
	}

	setVisible(visible: boolean) {
		this.image.setVisible(visible);
	}

	playFromStart() {}

	play() {}

	pause() {}
}

export class AnswerBoxView {
	readonly container: Phaser.GameObjects.Container;

	private readonly scene: Phaser.Scene;
	private readonly index: number;
	private readonly position: AnswerBoxPosition;
	private readonly themeConfig: LaneChooserAnswers;
	private readonly viewportWidth: number;
	private readonly renderScaleX: number;
	private readonly renderScaleY: number;
	private readonly animation: AnswerBoxVisual;
	private readonly label: Phaser.GameObjects.Text;
	private readonly deltaContainer: Phaser.GameObjects.Container;
	private readonly deltaIcon: Phaser.GameObjects.Image;
	private readonly deltaText: Phaser.GameObjects.Text;
	private readonly correctArrow: Phaser.GameObjects.Image;

	constructor({
		scene,
		parent,
		x,
		position,
		index,
		labelText,
		animationAsset,
		themeConfig,
		boxWidth,
		viewportWidth,
		renderScaleX,
		renderScaleY,
		lottieSourceRoot,
	}: AnswerBoxViewConfig) {
		this.scene = scene;
		this.index = index;
		this.position = position;
		this.themeConfig = themeConfig;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;

		const assetWidth = isLottieAnswerAsset(animationAsset)
			? animationAsset.canvasWidth
			: animationAsset.assetSize.width;
		const assetHeight = isLottieAnswerAsset(animationAsset)
			? animationAsset.canvasHeight
			: animationAsset.assetSize.height;
		const boxHeight = (boxWidth * assetHeight) / assetWidth;
		this.container = this.scene.add.container(x, 0);
		const animationContainer = this.scene.add.container(0, boxHeight / 2);
		this.animation = isLottieAnswerAsset(animationAsset)
			? new LottieCanvasSprite({
					scene: this.scene,
					parent: animationContainer,
					sourceRoot: lottieSourceRoot,
					textureKey: `${animationAsset.textureKey}-${index}`,
					animationData: animationAsset.animationData,
					canvasWidth: animationAsset.canvasWidth,
					canvasHeight: animationAsset.canvasHeight,
					loop: true,
					originX: 0.5,
					originY: 0.5,
			  })
			: new StaticAnswerBoxVisual(this.scene, animationContainer, animationAsset.textureKey);
		this.label = this.scene.add.text(0, 0, '', {
			align: 'center',
			color: this.themeConfig.textColor,
			fontFamily: LABEL_FONT_FAMILY,
			fontStyle: LABEL_FONT_STYLE,
		});
		this.deltaContainer = this.scene.add.container(0, 0);
		this.deltaIcon = this.scene.add.image(0, 0, CORRECT_ICON_TEXTURE_KEY);
		this.deltaText = this.scene.add.text(0, 0, '', {
			fontFamily: LABEL_FONT_FAMILY,
			fontStyle: LABEL_FONT_STYLE,
			fontSize: 14 * this.renderScaleX,
			color: DELTA_TEXT_COLOR,
		});
		this.correctArrow = this.scene.add.image(0, 0, CORRECT_ARROW_TEXTURE_KEY);
		const textResolution = Math.max(2, Math.ceil(this.renderScaleX));

		this.animation.setDisplaySize(boxWidth, boxHeight);
		this.animation.setFlipX(this.position === 'right' && !this.themeConfig.animations.right);
		this.animation.setVisible(true);
		this.animation.playFromStart();

		this.label.setOrigin(0.5, 0.5);
		this.label.setResolution(textResolution);
		syncAnswerLabelText({
			label: this.label,
			position: this.position,
			boxWidth,
			boxHeight,
			rawLabel: labelText,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
		});

		this.deltaIcon.setOrigin(0, 0.5);
		this.deltaIcon.setDisplaySize(16 * this.renderScaleX, 16 * this.renderScaleX);
		this.deltaText.setOrigin(0, 0.5);
		this.deltaText.setResolution(textResolution);
		this.deltaContainer.setVisible(false);
		this.deltaContainer.setAlpha(0);
		this.deltaContainer.add([this.deltaIcon, this.deltaText]);

		this.correctArrow.setOrigin(0.5, 0.5);
		this.correctArrow.setDisplaySize(18 * this.renderScaleX, 18 * this.renderScaleX);
		this.correctArrow.setVisible(false);
		this.correctArrow.setAlpha(0);
		this.syncFeedbackLayout();

		this.container.add([animationContainer, this.label, this.deltaContainer, this.correctArrow]);
		parent.add(this.container);
	}

	update() {
		this.animation.update();
	}

	destroy() {
		this.animation.destroy();
		this.container.destroy();
	}

	setAnimationPaused(paused: boolean) {
		if (paused) {
			this.animation.pause();
			return;
		}

		this.animation.play();
	}

	applyResolution(resolution: LaneChooserAnswerResolution) {
		this.hideFeedback();

		if (this.index === resolution.selectedIndex) {
			this.label.setColor(resolution.isCorrect ? ANSWER_SUCCESS_TEXT_COLOR : ANSWER_FAILED_TEXT_COLOR);
			this.showDeltaFeedback(resolution);
			return;
		}

		if (!resolution.isCorrect && this.index === resolution.correctIndex) {
			this.label.setColor(ANSWER_CORRECT_REVEAL_TEXT_COLOR);
			this.showCorrectArrow();
			return;
		}

		this.label.setColor(this.themeConfig.textColor);
	}

	private hideFeedback() {
		this.scene.tweens.killTweensOf(this.deltaContainer);
		this.scene.tweens.killTweensOf(this.correctArrow);
		this.deltaContainer.setVisible(false);
		this.deltaContainer.setAlpha(0);
		this.deltaContainer.setY(0);
		this.correctArrow.setVisible(false);
		this.correctArrow.setAlpha(0);
		this.correctArrow.setY(0);
	}

	private showDeltaFeedback(resolution: LaneChooserAnswerResolution) {
		const deltaLabel =
			typeof resolution.deltaSeconds === 'number' && resolution.deltaSeconds !== 0
				? resolution.deltaSeconds > 0
					? `+${resolution.deltaSeconds}`
					: `${resolution.deltaSeconds}`
				: '';
		const iconSize = this.viewportWidth >= SCREEN_SIZES.DESKTOP ? 24 : 16;
		const deltaFontSize = this.viewportWidth >= SCREEN_SIZES.DESKTOP ? 16 : 14;

		this.deltaIcon.setTexture(resolution.isCorrect ? CORRECT_ICON_TEXTURE_KEY : INCORRECT_ICON_TEXTURE_KEY);
		this.deltaIcon.setDisplaySize(iconSize * this.renderScaleX, iconSize * this.renderScaleX);
		this.deltaText.setText(deltaLabel);
		this.deltaText.setFontSize(deltaFontSize * this.renderScaleX);
		this.syncFeedbackLayout();
		this.deltaContainer.setVisible(true);
		this.deltaContainer.setAlpha(0);
		this.deltaContainer.setY(24 * this.renderScaleY);
		this.scene.tweens.add({
			targets: this.deltaContainer,
			alpha: 1,
			y: 0,
			duration: FEEDBACK_FADE_DURATION_MS,
			ease: 'Power1.out',
		});
	}

	private showCorrectArrow() {
		const arrowSize = this.viewportWidth >= SCREEN_SIZES.DESKTOP ? 28 : 18;

		this.correctArrow.setDisplaySize(arrowSize * this.renderScaleX, arrowSize * this.renderScaleX);
		this.syncFeedbackLayout();
		this.correctArrow.setVisible(true);
		this.correctArrow.setAlpha(0);
		this.correctArrow.setY(24 * this.renderScaleY);
		this.scene.tweens.add({
			targets: this.correctArrow,
			alpha: 1,
			y: 0,
			duration: FEEDBACK_FADE_DURATION_MS,
			ease: 'Power1.out',
		});
	}

	private syncFeedbackLayout() {
		const gap = 4 * this.renderScaleX;
		const totalWidth = this.deltaIcon.displayWidth + (this.deltaText.text ? gap + this.deltaText.width : 0);
		const iconX = -totalWidth / 2;

		this.deltaContainer.setPosition(0, 0);
		this.deltaIcon.setPosition(iconX, 0);
		this.deltaText.setPosition(iconX + this.deltaIcon.displayWidth + gap, 0);
		this.correctArrow.setPosition(0, 0);
	}
}
